/**
 * ChronicleReachLadder — DEV-1402 (epic content verification, Gate-1 bundle).
 *
 * The author's per-tier reach view, rendered on the content's own page (About
 * tab). Three stacked tiers, each with a status pill and the action that applies:
 *
 *   1. Your page & followers — reaching once published (existence).
 *   2. Community feed         — Gate 1, per-community (reach.communityFeedStatus).
 *   3. Search & discovery     — Gate 2 / expert (reach.searchStatus).
 *
 * "Publish = exists; validate = reaches." The ladder reads the server-computed
 * reach object (single source of truth); it never re-derives reach from raw flags.
 * Author-only (gated by `canEdit` upstream). Gates are PARALLEL — neither tier is
 * a prerequisite for the other.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  fetchMyCommunitiesForPicker,
  submitChronicleToCommunity,
  type ChronicleReach,
  type MyCommunityOption,
} from '../../api/chronicles';

type PillTone = 'reaching' | 'pending' | 'warning' | 'neutral';

interface Props {
  chronicleGuid: string;
  /** Existence: true when status === 'published'. Drives tier 1. */
  isPublished: boolean;
  /** Server-computed reach state (DEV-1402). Null while loading the parent. */
  reach: ChronicleReach | null | undefined;
  /** Submit-for-expert-review handler (the existing pool front door). */
  onSubmitForExpertReview: () => void;
  /** True while the expert-review submit is in flight. */
  submittingExpert: boolean;
  /** Whether expert review applies to this content (hidden for 'light' models). */
  canSubmitForExpertReview: boolean;
  /** DEV-1396 — whether the content has ≥1 subject tag. ≥1 is required before
   *  expert review (subjects route to matching reviewers). Gates the submit. */
  hasSubjects: boolean;
  /** Called after a successful submit-to-community so the parent can reload. */
  onCommunitySubmitted: () => void;
  /** Suppress the ladder's own heading when the host (e.g. ReachModal) already shows one. */
  hideTitle?: boolean;
}

const pillColors: Record<PillTone, { color: string; border: string }> = {
  reaching: { color: 'var(--color-success)', border: 'var(--color-success)' },
  pending: { color: 'var(--text-secondary)', border: 'var(--border-subtle)' },
  warning: { color: 'var(--color-warning)', border: 'var(--color-warning)' },
  neutral: { color: 'var(--text-tertiary)', border: 'var(--border-subtle)' },
};

function StatusPill({ label, tone }: { label: string; tone: PillTone }) {
  const c = pillColors[tone];
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '3px 9px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
        color: c.color,
        background: 'var(--bg-elevated)',
        border: `1px solid ${c.border}`,
      }}
    >
      {label}
    </span>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  border: '1px solid var(--accent-primary)',
  background: 'transparent',
  color: 'var(--accent-primary)',
  alignSelf: 'flex-start',
};

export default function ChronicleReachLadder({
  chronicleGuid,
  isPublished,
  reach,
  onSubmitForExpertReview,
  submittingExpert,
  canSubmitForExpertReview,
  hasSubjects,
  onCommunitySubmitted,
  hideTitle = false,
}: Props) {
  const { t } = useAppTranslation('pages');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [communities, setCommunities] = useState<MyCommunityOption[] | null>(null);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState<string>('');
  const [submittingCommunity, setSubmittingCommunity] = useState(false);

  // Lazy-load the picker options only when the picker opens.
  useEffect(() => {
    if (!pickerOpen || communities !== null) return;
    let cancelled = false;
    setLoadingCommunities(true);
    fetchMyCommunitiesForPicker()
      .then((opts) => {
        if (cancelled) return;
        setCommunities(opts);
      })
      .catch(() => {
        if (cancelled) return;
        setCommunities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCommunities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, communities]);

  const handleSubmitToCommunity = useCallback(() => {
    if (!selectedCommunity) return;
    setSubmittingCommunity(true);
    submitChronicleToCommunity(chronicleGuid, selectedCommunity)
      .then(() => {
        window.alert(t('chronicleView.reach.submitToCommunitySuccess'));
        setPickerOpen(false);
        setSelectedCommunity('');
        onCommunitySubmitted();
      })
      .catch(() => window.alert(t('chronicleView.reach.submitToCommunityError')))
      .finally(() => setSubmittingCommunity(false));
  }, [chronicleGuid, selectedCommunity, t, onCommunitySubmitted]);

  const communityName = reach?.communityName ?? '';

  // ── Tier 2 (community feed) status pill + body + action ──
  const communityStatus = reach?.communityFeedStatus ?? 'none';
  let communityPill: { label: string; tone: PillTone } | null = null;
  let communityBody = '';
  switch (communityStatus) {
    case 'reaching':
      communityPill = { label: t('chronicleView.reach.statusReaching'), tone: 'reaching' };
      communityBody = t('chronicleView.reach.communityReaching', { community: communityName });
      break;
    case 'pending':
      communityPill = { label: t('chronicleView.reach.statusPending'), tone: 'pending' };
      communityBody = t('chronicleView.reach.communityPending', { community: communityName });
      break;
    case 'eligible':
      communityPill = { label: t('chronicleView.reach.statusNotSubmitted'), tone: 'neutral' };
      communityBody = t('chronicleView.reach.communityEligible', { community: communityName });
      break;
    case 'suspended':
      communityPill = { label: t('chronicleView.reach.statusSuspended'), tone: 'warning' };
      communityBody = t('chronicleView.reach.communitySuspended', { community: communityName });
      break;
    case 'none':
    default:
      communityPill = null;
      communityBody = t('chronicleView.reach.communityNone');
      break;
  }

  // ── Tier 3 (search & discovery) status pill + body + action ──
  const searchStatus = reach?.searchStatus ?? 'not_submitted';
  let searchPill: { label: string; tone: PillTone } | null = null;
  let searchBody = '';
  switch (searchStatus) {
    case 'reaching':
      searchPill = { label: t('chronicleView.reach.statusReaching'), tone: 'reaching' };
      searchBody = t('chronicleView.reach.searchReaching');
      break;
    case 'pending':
      searchPill = { label: t('chronicleView.reach.statusPending'), tone: 'pending' };
      searchBody = t('chronicleView.reach.searchPending');
      break;
    case 'changes_requested':
      searchPill = { label: t('chronicleView.reach.statusChangesRequested'), tone: 'warning' };
      searchBody = t('chronicleView.reach.searchChangesRequested');
      break;
    case 'suspended':
      searchPill = { label: t('chronicleView.reach.statusSuspended'), tone: 'warning' };
      searchBody = t('chronicleView.reach.searchSuspended');
      break;
    case 'not_submitted':
    default:
      searchPill = null;
      searchBody = t('chronicleView.reach.searchNotSubmitted');
      break;
  }

  // The expert-review submit/resubmit rung shows when expert review applies and
  // the content is in a submittable search state (not_submitted/changes_requested/suspended).
  const showExpertAction =
    canSubmitForExpertReview &&
    (searchStatus === 'not_submitted' || searchStatus === 'changes_requested' || searchStatus === 'suspended');
  const expertActionLabel =
    searchStatus === 'suspended'
      ? t('chronicleView.reach.resubmit')
      : t('chronicleView.reach.submitForExpertReview');

  const tierWrap: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: '14px 16px',
    borderRadius: 10,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-subtle)',
  };
  const tierHeader: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  };
  const tierTitle: React.CSSProperties = { fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' };
  const tierBody: React.CSSProperties = { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 };

  return (
    <section
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 12,
        padding: 16,
      }}
    >
      {!hideTitle && (
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>
          {t('chronicleView.reach.title')}
        </div>
      )}

      {/* ── Tier 1: Your page & followers (existence) ── */}
      <div style={tierWrap}>
        <div style={tierHeader}>
          <span style={tierTitle}>{t('chronicleView.reach.tierPageTitle')}</span>
          {isPublished && (
            <StatusPill label={t('chronicleView.reach.statusReaching')} tone="reaching" />
          )}
        </div>
        <div style={tierBody}>
          {isPublished
            ? t('chronicleView.reach.tierPageReaching')
            : t('chronicleView.reach.tierPagePublishHint')}
        </div>
      </div>

      {/* ── Tier 2: Community feed (Gate 1) ── */}
      <div style={tierWrap}>
        <div style={tierHeader}>
          <span style={tierTitle}>{t('chronicleView.reach.tierCommunityTitle')}</span>
          {communityPill && <StatusPill label={communityPill.label} tone={communityPill.tone} />}
        </div>
        <div style={tierBody}>{communityBody}</div>

        {/* No target community yet → the submit-to-community affordance (the action
            that finally associates personal content with a community). */}
        {communityStatus === 'none' && !pickerOpen && (
          <button type="button" style={actionBtnStyle} onClick={() => setPickerOpen(true)}>
            {t('chronicleView.reach.submitToCommunity')}
          </button>
        )}

        {/* Suspended → resubmit-to-community path (re-association is the same action). */}
        {communityStatus === 'suspended' && !pickerOpen && (
          <button type="button" style={actionBtnStyle} onClick={() => setPickerOpen(true)}>
            {t('chronicleView.reach.resubmit')}
          </button>
        )}

        {/* Community picker — inline, opaque (sits inside an opaque parent surface). */}
        {pickerOpen && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              marginBlockStart: 4,
              padding: 12,
              borderRadius: 8,
              backgroundColor: 'var(--bg-primary)',
              backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
              border: '1px solid var(--border-default)',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-primary)' }}>
              {t('chronicleView.reach.pickerTitle')}
            </div>
            {loadingCommunities ? (
              <div style={tierBody}>{t('chronicleView.reach.pickerLoading')}</div>
            ) : (communities ?? []).length === 0 ? (
              <div style={tierBody}>{t('chronicleView.reach.pickerEmpty')}</div>
            ) : (
              <select
                value={selectedCommunity}
                onChange={(e) => setSelectedCommunity(e.target.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border-default)',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  fontSize: 13,
                }}
              >
                <option value="">{t('chronicleView.reach.pickerTitle')}</option>
                {(communities ?? []).map((c) => (
                  <option key={c.communityGuid} value={c.communityGuid}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={handleSubmitToCommunity}
                disabled={!selectedCommunity || submittingCommunity}
                style={{
                  ...actionBtnStyle,
                  opacity: !selectedCommunity || submittingCommunity ? 0.5 : 1,
                  cursor: !selectedCommunity || submittingCommunity ? 'default' : 'pointer',
                }}
              >
                {submittingCommunity ? '…' : t('chronicleView.reach.pickerSubmit')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPickerOpen(false);
                  setSelectedCommunity('');
                }}
                style={{
                  ...actionBtnStyle,
                  border: '1px solid var(--border-default)',
                  color: 'var(--text-secondary)',
                }}
              >
                {t('chronicleView.reach.pickerCancel')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Tier 3: Search & discovery (Gate 2 / expert) ── */}
      <div style={tierWrap}>
        <div style={tierHeader}>
          <span style={tierTitle}>{t('chronicleView.reach.tierSearchTitle')}</span>
          {searchPill && <StatusPill label={searchPill.label} tone={searchPill.tone} />}
        </div>
        <div style={tierBody}>{searchBody}</div>
        {showExpertAction && (
          <>
            {/* DEV-1396 — subjects route content to matching reviewers, so ≥1 is
                required before expert review. When none, show an actionable hint
                pointing to the Subjects editor and disable the submit. */}
            {!hasSubjects && (
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--color-warning)',
                  lineHeight: 1.6,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--color-warning)',
                }}
              >
                {t('chronicleView.subjects.submitHint')}
              </div>
            )}
            <button
              type="button"
              onClick={onSubmitForExpertReview}
              disabled={submittingExpert || !hasSubjects}
              title={!hasSubjects ? t('chronicleView.subjects.submitHint') : undefined}
              style={{
                ...actionBtnStyle,
                opacity: submittingExpert || !hasSubjects ? 0.5 : 1,
                cursor: submittingExpert || !hasSubjects ? 'default' : 'pointer',
              }}
            >
              {submittingExpert ? '…' : expertActionLabel}
            </button>
          </>
        )}
      </div>

      {/* Reassurance — the floor never drops; not-validated ≠ false. */}
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
        {t('chronicleView.reach.reassurance')}
      </div>
    </section>
  );
}
